/**
 * WHAT AN OPERATOR RECORD IS FOR — the screen's copy of the API's
 * `operatorType.helper`.
 *
 * About ten of the 130 records in the operators list are not suppliers.
 * `AU-AA-608 Parkolás`, `AU-AA-670 Bérlési dij`, `Agent commission` and the
 * rest exist so an own-fleet cost has somewhere to land. They have always sat
 * in the same list as real suppliers, which is why a trip can currently be
 * dispatched to "Shell Tankolás".
 *
 * THE DISTINCTION IS NARROW AND EASY TO GET BACKWARDS. Every operator picker
 * in this app is one of four kinds, and which one decides whether an expense
 * record belongs in it:
 *
 *   ASSIGNMENT — who DOES the work: the booking's operator, a role slot on a
 *                multi-operator booking, a batch assignment on an event, the
 *                dispatcher's assign sheet, a request for a quote. An
 *                `expense` record must NOT be offered.
 *
 *   COST       — where MONEY LANDS: an order-line adjustment's operator, a
 *                receipt's cost destination, a payment's counterparty. An
 *                `expense` record MUST still be offered. That is what these
 *                records are for, and filtering them out here would break
 *                own-fleet cost attribution outright.
 *
 *   ROUTING    — which bucket a cost type on a VEHICLE is charged to, i.e.
 *                `Car.cost_operators` in the fleet modal. The mirror image
 *                of ASSIGNMENT: ONLY an `expense` record may be offered.
 *                Naming a supplier there would file the driver's parking as
 *                money owed to that supplier, and it would be paid.
 *
 *   NEITHER    — list filters, id→name lookups, the operators list itself.
 *                Every record, unchanged: an expense account's bookings are
 *                real and must stay findable.
 *
 * So a picker reaches for `assignableOperators` or `expenseAccounts` and a
 * COST picker for neither. The names are the safeguard:
 * `.filter(isAssignableOperator)` reads wrong on a cost picker in a way
 * `.filter(o => o.operator_type !== 'expense')` does not, and
 * `expenseAccounts(operators)` reads wrong anywhere a supplier belongs.
 */

/** The two things an operator record can be. Must match the API's enum. */
export const OPERATOR_TYPES = ['operator', 'expense'];

/**
 * What a record with nothing stored is.
 *
 * `operator` — so nothing is reclassified by this shipping. Every one of the
 * 130 existing records carries no value and therefore reads as a supplier,
 * which is what they read as today. A different default here would empty
 * every assignment picker in the app on deploy.
 */
export const DEFAULT_OPERATOR_TYPE = 'operator';

/** A record's type, however little the record carries. */
export const operatorTypeOf = (operator) => (
  OPERATOR_TYPES.includes(operator?.operator_type)
    ? operator.operator_type
    : DEFAULT_OPERATOR_TYPE
);

/** A cost bucket rather than a supplier. */
export const isExpenseAccount = (operator) => operatorTypeOf(operator) === 'expense';

/**
 * May a trip be dispatched to this record?
 *
 * The complement of `isExpenseAccount`, named for the question the assignment
 * pickers are actually asking so a call site cannot read as the wrong one.
 */
export const isAssignableOperator = (operator) => !isExpenseAccount(operator);

/**
 * The records an ASSIGNMENT picker may offer. Cost pickers must not call it.
 *
 * Tolerates a null/undefined list so a picker can call it on its first render,
 * before the operators have loaded, exactly as it would have mapped an empty
 * array.
 */
export const assignableOperators = (operators) => (operators || []).filter(isAssignableOperator);

/**
 * The records a ROUTING picker may offer — cost buckets and nothing else.
 *
 * The exact complement of `assignableOperators`, and deliberately not
 * expressed as "not assignable": the danger here is a SUPPLIER getting in,
 * so the filter names what is allowed rather than what is excluded.
 *
 * Tolerates a null/undefined list for the same reason as its twin: a picker
 * renders once before the operators have loaded.
 */
export const expenseAccounts = (operators) => (operators || []).filter(isExpenseAccount);

/** What each type is called on screen. */
export const OPERATOR_TYPE_LABELS = {
  operator: 'Operator (a supplier we give work to)',
  expense: 'Expense account (a cost bucket — never given work)',
};

/** The short form, for a badge or a list column. */
export const OPERATOR_TYPE_SHORT_LABELS = {
  operator: 'Operator',
  expense: 'Expense account',
};

/**
 * THE ROLE AN EXPENSE ACCOUNT MAY NEVER HOLD.
 *
 * `driver` is not a label on a booking, it is a position: `getPrimaryOperator`
 * returns the driver slot as the booking's primary, so an expense account in
 * one re-homes the whole trip to a parking bucket. Nothing in the data model
 * stops it, which is why the UI steers and the API refuses (ph-core
 * `OperatorAssignmentGuard`).
 */
export const DRIVER_ROLE = 'driver';

/**
 * THE COST ROLE — how an `operators[]` entry says it is on the booking
 * because money lands on it, not because anybody works the trip.
 *
 * The API's `EXPENSE_ROLE` (ph-core `Helpers/BookingType.helper.ts`),
 * copied because the two repos share no code: change one, change both. It is
 * what the API's `roleForExpenseLine` files a receipt's operator under when
 * that operator is not already on the booking, and what this screen steers a
 * cost account to.
 *
 * Until 2026-11-03 there was no such role and both used `assistant`, which
 * stored a parking bucket as a person who assisted on the trip (Mark's
 * decision to add it: 2026-11-02/03). `assistant` is still a crew role, for
 * real people.
 *
 * It is NOT `operator_type: 'expense'`. The type says what a RECORD is, the
 * role why an ENTRY is on a booking. A human may not give the cost role to a
 * supplier (`MultiOperatorAssignment` bars it), but the receipt path can, for
 * a cost filed against an operator that is not on the trip.
 */
export const EXPENSE_ROLE = 'expense';

/**
 * The role a cost account takes when it is chosen into a row: the role it
 * ALREADY holds elsewhere on the booking, else `expense`. Never `driver`
 * (see `DRIVER_ROLE`).
 *
 * The same rule as the API's `roleForExpenseLine`, so a bucket the office
 * attaches by hand lands in the slot a receipt approval would have joined,
 * rather than opening a second one beside it. A bucket still stored as
 * `assistant` (before the stored data is converted) is therefore joined as
 * `assistant`, exactly as the API would.
 */
export const steeredExpenseRole = (bookingOperators, operatorId, skipIndex = -1) => {
  const existing = (bookingOperators || []).find((op, i) => (
    i !== skipIndex
    && op?.operator_id
    && String(op.operator_id) === String(operatorId)
    && op.role
    && op.role !== DRIVER_ROLE
  ));

  return existing?.role || EXPENSE_ROLE;
};
